import { useEffect, useRef } from 'react'

interface Particle {
  id: number
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  color: string
}

// Performance optimization constants - 2025-01-XX
const MAX_PARTICLES = 250 // Limit particle count to prevent performance degradation
const PARTICLES_PER_MOVE = 3 // Reduced from 4 for better performance
const MOUSE_THROTTLE_MS = 16 // ~60fps for mouse events
const COLORS = ['#00d4ff', '#00ffff', '#ffffff', '#88ffff', '#00aaff'] as const

export function CursorTrail() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const particlesRef = useRef<Particle[]>([])
  const mouseRef = useRef({ x: 0, y: 0 })
  const animationRef = useRef<number>()
  const particleIdRef = useRef(0)
  const lastMouseMoveRef = useRef(0)
  const isAnimatingRef = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { 
      alpha: true,
      desynchronized: true // Better performance for animations
    })
    if (!ctx) return

    // Set canvas size with device pixel ratio support - 2025-01-XX
    const dpr = window.devicePixelRatio || 1
    const resizeCanvas = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      
      ctx.scale(dpr, dpr)
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas, { passive: true })

    // Throttled mouse move handler - 2025-01-XX
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now()
      if (now - lastMouseMoveRef.current < MOUSE_THROTTLE_MS) return
      lastMouseMoveRef.current = now
      
      mouseRef.current = { x: e.clientX, y: e.clientY }
      
      // Limit particle creation if we have too many - 2025-01-XX
      const currentParticleCount = particlesRef.current.length
      if (currentParticleCount >= MAX_PARTICLES) {
        // Remove oldest particles if we're at max
        particlesRef.current = particlesRef.current.slice(-MAX_PARTICLES + PARTICLES_PER_MOVE)
      }
      
      // Create new particles
      for (let i = 0; i < PARTICLES_PER_MOVE; i++) {
        const particle: Particle = {
          id: particleIdRef.current++,
          x: e.clientX + (Math.random() - 0.5) * 12,
          y: e.clientY + (Math.random() - 0.5) * 12,
          vx: (Math.random() - 0.5) * 2.5,
          vy: (Math.random() - 0.5) * 2.5,
          life: 0,
          maxLife: 50 + Math.random() * 30,
          size: 1.5 + Math.random() * 2,
          color: COLORS[Math.floor(Math.random() * COLORS.length)]
        }
        particlesRef.current.push(particle)
      }
      
      // Start animation loop if not already running - 2025-01-XX
      if (!isAnimatingRef.current && particlesRef.current.length > 0) {
        isAnimatingRef.current = true
        animate()
      }
    }

    // Optimized animation loop - 2025-01-XX
    const animate = () => {
      if (!ctx || !canvas) {
        isAnimatingRef.current = false
        return
      }
      
      const particles = particlesRef.current
      if (particles.length === 0) {
        isAnimatingRef.current = false
        return
      }
      
      // Clear canvas (using scaled dimensions)
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr)
      
      // Update and filter particles in a single pass
      const aliveParticles: Particle[] = []
      
      for (let i = 0; i < particles.length; i++) {
        const particle = particles[i]
        
        // Update particle physics
        particle.x += particle.vx
        particle.y += particle.vy
        particle.vx *= 0.95
        particle.vy *= 0.95
        particle.life++
        
        // Calculate alpha once
        const progress = particle.life / particle.maxLife
        const alpha = Math.max(0, 1 - progress)
        
        if (alpha > 0 && particle.life < particle.maxLife) {
          // Batch drawing operations to reduce context state changes - 2025-01-XX
          ctx.save()
          
          // Holographic outer glow
          ctx.globalAlpha = alpha * 0.8
          ctx.fillStyle = particle.color
          ctx.shadowBlur = 12
          ctx.shadowColor = particle.color
          ctx.beginPath()
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
          ctx.fill()
          
          // Holographic scan lines (optimized to 2 lines) - 2025-01-XX
          ctx.globalAlpha = alpha * 0.5
          ctx.strokeStyle = particle.color
          ctx.lineWidth = 0.5
          ctx.shadowBlur = 4
          const sizeHalf = particle.size * 0.5
          for (let j = 0; j < 2; j++) {
            const y = particle.y - particle.size + sizeHalf + (j * particle.size * 0.5)
            ctx.beginPath()
            ctx.moveTo(particle.x - particle.size, y)
            ctx.lineTo(particle.x + particle.size, y)
            ctx.stroke()
          }
          
          // Holographic core
          ctx.globalAlpha = alpha * 0.9
          ctx.fillStyle = '#ffffff'
          ctx.shadowBlur = 6
          ctx.shadowColor = '#ffffff'
          ctx.beginPath()
          ctx.arc(particle.x, particle.y, particle.size * 0.3, 0, Math.PI * 2)
          ctx.fill()
          
          ctx.restore()
          
          aliveParticles.push(particle)
        }
      }
      
      particlesRef.current = aliveParticles
      
      // Continue animation if there are particles, otherwise stop - 2025-01-XX
      if (aliveParticles.length > 0) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        isAnimatingRef.current = false
      }
    }

    // Add event listeners with passive flag for better performance - 2025-01-XX
    document.addEventListener('mousemove', handleMouseMove, { passive: true })

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas)
      document.removeEventListener('mousemove', handleMouseMove)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      isAnimatingRef.current = false
      particlesRef.current = []
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none"
      style={{ 
        background: 'transparent',
        zIndex: 20 // High z-index to appear above all content
      }}
    />
  )
}
